import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";

import { DECISION_OPTIONS, getDecisionColor } from "../../utils/codingOpStorage";
import { formatDateTime } from "../../../../utils/formatters/date";

export default function EditDecisionDialog({
  open,
  onClose,
  opportunity,
  currentOverride,
  onSave,
  onRemoveOverride,
}) {
  const [decision, setDecision] = useState("");
  const [comment, setComment] = useState("");

  // Initialize state when dialog opens or opportunity changes
  useEffect(() => {
    if (open && opportunity) {
      // If there's an existing override, use it; otherwise use AI decision
      if (currentOverride) {
        setDecision(currentOverride.decision || "");
        setComment(currentOverride.comment || "");
      } else {
        setDecision(opportunity.decision || "");
        setComment("");
      }
    }
  }, [open, opportunity, currentOverride]);

  const handleSave = () => {
    if (!decision) return;

    onSave({
      decision,
      comment,
      originalDecision: opportunity?.decision || null,
    });
  };

  const handleRemoveOverride = () => {
    onRemoveOverride();
  };

  const hasOverride = !!currentOverride;
  const isModified = decision !== opportunity?.decision;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <EditIcon color="primary" />
          <span>Edit Decision</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {opportunity && (
          <Box>
            {/* Opportunity Info */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Coding Opportunity
              </Typography>
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                {opportunity.code || "—"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {opportunity.description || "No description"}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* AI Original Decision */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                AI Decision
              </Typography>
              <Chip
                label={opportunity.decision || "—"}
                color={getDecisionColor(opportunity.decision)}
                size="small"
              />
              {opportunity.confidence !== undefined && (
                <Chip
                  label={`${Math.round((Number(opportunity.confidence) || 0) * 100)}% confidence`}
                  variant="outlined"
                  size="small"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>

            {/* Override Form */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="decision-label">Doctor Decision</InputLabel>
              <Select
                labelId="decision-label"
                value={decision}
                label="Doctor Decision"
                onChange={(e) => setDecision(e.target.value)}
              >
                {DECISION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={opt.label}
                        color={opt.color}
                        sx={{ minWidth: 100, pointerEvents: "none" }}
                      />
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Comment (optional)"
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment explaining your decision..."
              sx={{ mb: 2 }}
            />

            {isModified && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This will override the AI decision from "{opportunity.decision}" to "{decision}"
              </Alert>
            )}

            {hasOverride && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This opportunity has been previously modified on{" "}
                {formatDateTime(currentOverride.overriddenAt)}
                {currentOverride.overriddenBy && ` by ${currentOverride.overriddenBy}`}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {hasOverride && (
          <Button
            color="warning"
            startIcon={<UndoIcon />}
            onClick={handleRemoveOverride}
            sx={{ mr: "auto" }}
          >
            Remove Override
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!decision}
        >
          Save Decision
        </Button>
      </DialogActions>
    </Dialog>
  );
}
